import { call, put, select, take, takeLatest } from 'redux-saga/effects'
import { buyCheckoutActions, BuyCheckoutActionTypes } from '@buy/state/checkout/actions'
import { getTokenFromLocalStorage } from '@common/utils'
import { fetchInvoiceAsBlob } from '@buy/services'
import { AxiosResponse } from 'axios'
import { BuyCheckoutState } from '@buy/state/checkout/state'
import { getCheckoutState } from '@buy/state/selectors'

export function* downloadInvoiceSaga() {
    // Show loading indicator
    yield put(buyCheckoutActions.setDownloadingInvoice(true))

    // Get checkout state so we can check if invoice metadata has been downloaded yet
    let checkoutState: BuyCheckoutState = yield select(getCheckoutState)

    // If invoice metadata hasn't finished being downloaded yet, then wait for it to finish
    if (checkoutState.invoice == null) {
        // Wait for SET_INVOICE to be called after the invoice metadata finishes being
        // downloaded since it was already triggered on the page load of the confirmation page.
        // See getLatestInvoice() on src/products/buy/views/Confirmation/index.tsx
        yield take(BuyCheckoutActionTypes.SET_INVOICE)

        // Get checkout state again so we can retrieve the newly set invoice metadata
        checkoutState = yield select(getCheckoutState)
    }

    const { invoice } = checkoutState

    // if invoice metadata still isn't populated by this time, something is wrong
    if (invoice == null) {
        console.error('No invoice loaded')
    }

    // Get token from local storage.
    // See where it gets set after silent auth in src/common/utils/auth-class.ts
    const token = getTokenFromLocalStorage()
    const userToken = token?.access_token
    if (!userToken) {
        console.error('Missing token')
        return
    }

    // Retrieve accountKey from sessionStorage.
    // See where it gets set before redirecting to OnePay in src/products/buy/saga/checkout-form/form-submission.ts
    const accountKey = window.sessionStorage.getItem('accountKey')
    if (!accountKey) {
        console.error('Missing accountKey')
        return
    }

    // InvoiceID is a number in the API response. Convert it to string here
    const invoiceID = invoice?.invoiceID?.toString() ?? ''

    try {
        // Fetch the invoice pdf blob contents
        const fetchResponse: AxiosResponse = yield call(fetchInvoiceAsBlob, userToken, accountKey, invoiceID)
        const blob = fetchResponse.data

        // Get filename ready
        const header = fetchResponse.headers['content-disposition']
        let filename = 'invoice'
        if (header) {
            filename = header.split('filename=')?.[1]
            filename = filename.replace(/['"]+/g, '')
            filename = filename.split('.')?.[0]
        }

        // Prompt to download PDF
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = filename
        a.click()
        a.remove()
    } catch (ex) {
        console.error('Error downloading invoice', ex)
    } finally {
        // Hide loading indicator
        yield put(buyCheckoutActions.setDownloadingInvoice(false))
    }
}

function* initializeDownloadInvoiceSaga() {
    yield takeLatest(BuyCheckoutActionTypes.DOWNLOAD_INVOICE, downloadInvoiceSaga)
}

export default initializeDownloadInvoiceSaga
